import { sendTracking} from "../../utilities/js/tracking";
import './gated-content-form.scss';

class GatedContentForm {
    constructor (element, options) {
        // Defaults & Settings
        const defaults = {
            initAttr: 'data-gatedcontentform',
            prevControlValue: 'prev',
            nextControlValue: 'next',
            closeControlValue: 'close',
            scrollerValue: 'scroller',
            stepperValue: 'stepper',
            stepsValue: 'steps',
            stepValue: 'step',
            successValue: 'success',
            modalValue: 'data-gatedcontentform-modal'
        };

        this.settings = Object.assign({}, defaults, options);

        // Elements
        this.$root = element;
        this.$form = this.$root.closest('form');
        this.$submittedTarget = null;
        this.$stepper = this.$root.querySelector(`[${this.settings.initAttr}="${this.settings.stepperValue}"]`);
        this.$$steps = this.$root.querySelectorAll(`[${this.settings.initAttr}="${this.settings.stepValue}"]`);
        this.$$prevControls = this.$form?.querySelectorAll(`[${this.settings.initAttr}="${this.settings.prevControlValue}"]`);
        this.$nextControl = this.$form?.querySelector(`[${this.settings.initAttr}="${this.settings.nextControlValue}"]`);
        this.$closeControl = this.$form?.querySelector(`[${this.settings.initAttr}="${this.settings.closeControlValue}"]`);
        this.$modalScroller = this.$form?.querySelector(`[${this.settings.initAttr}="${this.settings.scrollerValue}"]`);
        this.$modal = this.$form?.closest('dialog');
        this.$submitControl = this.$form?.querySelector(`button[type="submit"]`);
        this.$$duplicateSelects = this.$form?.querySelectorAll('input[name*="_duplicate"]');
        this.$successScreen = null;

        // Properties
        this.activeStepIndex = 0;
        this.stepperAPI = null;
        this.modalAPI = null;
        this.isMultiStep = this.$$steps.length > 2;
        this.isModal = this.$root.hasAttribute(this.settings.modalValue);
        this.isSubmitted = false;
        this.formType = this.isMultiStep ? 'expanded' : 'default';

        if (!this.$form) return;
    }

    initialize () {
        this.handleSubmittedParam();

        if (this.isSubmitted) {
            this.focusTarget();
        } else if (this.isModal) {
            this.initializeStepper(() => {
                this.goToStep();
            });
        }

        this.setEvents();

        // Ensure any pre-selected duplicate radios are synced on init
        if (this.isModal) {
            this.syncSelects();
        }
    }

    syncSelects() {
        if (!this.$$duplicateSelects || this.$$duplicateSelects.length === 0) return;

        // Group duplicate radios by their name and sync each corresponding select
        const groups = {};
        Array.from(this.$$duplicateSelects).forEach(($radio) => {
            const name = $radio.getAttribute('name');
            if (!groups[name]) groups[name] = [];
            groups[name].push($radio);
        });

        Object.keys(groups).forEach((radioName) => {
            const group = groups[radioName];
            const $selectedRadio = group.find($r => $r.checked);
            if (!$selectedRadio) return;

            const radioValue = $selectedRadio.value;
            const selectName = radioName.replace(/_duplicate$/, '');

            const $select = this.$form.querySelector(`select[name="${selectName}"]`);
            if (!$select) return;

            // Set the select's value to the selected radio's value if an option matches
            const $matchingOption = Array.from($select.options).find(option => option.value === radioValue);
            if ($matchingOption) {
                $select.value = $matchingOption.value;
                $matchingOption.selected = true;
            }
        });
    }

    initializeStepper(callback) {
        const runCallback = () => typeof callback === 'function' && callback();

        if (!this.$stepper) return runCallback();

        import('../stepper/stepper')
            .then(({ Stepper }) => {
                this.stepperAPI = new Stepper(this.$stepper);
                runCallback();
            })
            .catch(error => console.error('Error loading Stepper instance', error));
    }

    goToStep () {
        if (this.stepperAPI !== null) {
            this.stepperAPI.updateStepper(this.activeStepIndex);
        }

        this.toggleStepContent();
    }

    toggleStepContent () {
        this.$root.style.setProperty('--active-step', this.activeStepIndex);

        if (this.$modalScroller) {
            this.$modalScroller.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }

        this.$$steps.forEach(($step, index) => {
            if (this.activeStepIndex === index) {
                $step?.removeAttribute('inert');
            } else {
                $step?.setAttribute('inert', '');
            }
        });

        this.checkValidity();
    }

    checkValidity() {
        let sectionIsValid = true;

        const $$formFields = this.$$steps[this.activeStepIndex]?.querySelectorAll('input, select, textarea');
        $$formFields.forEach($formField => {
            if (!$formField.checkValidity()) sectionIsValid = false;
        });

        const setAttr = ($button, hidden, disabled) => {
            if (!$button) return;
            hidden ? $button.setAttribute('hidden', '') : $button.removeAttribute('hidden');
            disabled ? $button.setAttribute('disabled', '') : $button.removeAttribute('disabled');
        };

        const isLast = this.activeStepIndex === this.$$steps.length - 1;

        if (this.isMultiStep) {
            const isFirst = this.activeStepIndex === 0;
            const isPenultimate = this.activeStepIndex === this.$$steps.length - 2;

            if (isFirst) {
                this.$$prevControls?.forEach($prevControl => {
                    setAttr($prevControl, false, true);
                });
                setAttr(this.$nextControl, false, sectionIsValid ? false : true);
                setAttr(this.$submitControl, true, true);
                setAttr(this.$closeControl, true, true);
            } else if (isPenultimate) {
                this.$$prevControls?.forEach($prevControl => {
                    setAttr($prevControl, false, false);
                });
                setAttr(this.$nextControl, true, true);
                setAttr(this.$submitControl, false, sectionIsValid ? false : true);
                setAttr(this.$closeControl, true, true);
            } else if (isLast) {
                this.$$prevControls?.forEach($prevControl => {
                    setAttr($prevControl, true, true);
                });
                setAttr(this.$nextControl, true, true);
                setAttr(this.$submitControl, true, true);
                setAttr(this.$closeControl, false, false);
            } else {
                this.$$prevControls?.forEach($prevControl => {
                    setAttr($prevControl, false, false);
                });
                setAttr(this.$nextControl, false, sectionIsValid ? false : true);
                setAttr(this.$submitControl, true, true);
                setAttr(this.$closeControl, true, true);
            }
        } else {
            if (isLast) {
                setAttr(this.$submitControl, true, true);
                setAttr(this.$closeControl, false, false);
            } else {
                setAttr(this.$submitControl, false, sectionIsValid ? false : true);
                setAttr(this.$closeControl, true, true);
            }
        }
    }

    handleSubmittedParam() {
        const url = new URL(window.location.href);

        let submittedValue = null;
        if (url.searchParams.has('submitted')) {
            submittedValue = url.searchParams.get('submitted');
        }

        if (submittedValue !== null) {
            this.$submittedTarget = document.getElementById(submittedValue);
            this.$successScreen = document.getElementById(`${submittedValue}-success`);

            if (!this.$submittedTarget || !this.$successScreen) return;

            if (this.$submittedTarget?.contains(this.$root)) {
                url.searchParams.delete('submitted');
                window.history.replaceState({}, '', url.toString());
                this.$submittedTarget.scrollIntoView({ behavior: "auto" });
                this.isSubmitted = true;
            }
        }
    }

    focusTarget () {
        if (this.isModal) {
            window.addEventListener('modal.ready', (e) => {
                const eventDetails = e.detail;
                const $modalTarget = eventDetails.modalTarget;
                this.activeStepIndex = this.$$steps.length - 1;

                if ($modalTarget === this.$submittedTarget) {
                    this.modalAPI = eventDetails.api;
                    this.modalAPI.openModal();
                }

                this.initializeStepper(() => {
                    this.goToStep();
                });
            });

            window.addEventListener('modal.closed', () => {
                if (this.activeStepIndex === this.$$steps.length - 1) {
                    this.activeStepIndex = 0;
                    this.goToStep();
                }
            });
        } else {
            if (!this.$successScreen) return;
            this.$successScreen.removeAttribute('inert');
            this.$submittedTarget.setAttribute('inert', '');
        }
    }

    prepareAndSendTrackingData (eventKey, label = null) {
        let trackingData = {
            event: `gated_content_${eventKey}`,
            elementCategory: this.formType
        };

        if (label !== null) {
            trackingData.elementText = label;
        }

        sendTracking(trackingData);
    }

    setEvents () {
        if (this.isModal) {
            this.$form?.addEventListener('change', () => {
                this.checkValidity();
            });

            this.$$prevControls?.forEach($prevControl => {
                $prevControl?.addEventListener('click', () => {
                    this.activeStepIndex--;
                    this.prepareAndSendTrackingData(`step_${this.activeStepIndex + 1}`, 'backward');
                    this.goToStep();
                });
            });

            this.$nextControl?.addEventListener('click', () => {
                this.activeStepIndex++;
                this.prepareAndSendTrackingData(`step_${this.activeStepIndex + 1}`, 'forward');
                this.goToStep();
            });

            this.$closeControl?.addEventListener('click', () => {
                this.modalAPI.closeModal();
            });

            this.$form?.addEventListener('change', () => {
                this.syncSelects();
            });

            window.addEventListener('modal.opened', (e) => {
                if (e.detail.$context === this.$modal) {
                    this.prepareAndSendTrackingData('step_1');
                }
            });

            window.addEventListener('modal.closed', (e) => {
                if (e.detail.$context === this.$modal && this.isSubmitted !== true) {
                    this.prepareAndSendTrackingData('cancel', this.activeStepIndex + 1);
                }
            });

            this.$form.addEventListener('form.submitted', () => {
                this.prepareAndSendTrackingData('submit');

            });
        }
    }
}

export { GatedContentForm };

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $$roots = $context.querySelectorAll('[data-gatedcontentform="root"]');
        $$roots.forEach($root => {
            const $rootAPI = new GatedContentForm($root);
            $rootAPI.initialize();
        });
    }
});
